import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { getProp } from "@cervval.js/common/src/tools/any.tools";
import { isInside, isNumber } from "@cervval.js/common/src/tools/number.tools";
import { SECONDS_IN_ONE_DAY, SECONDS_IN_ONE_HOUR, SECONDS_IN_ONE_WEEK } from "@cervval.js/common/src/tools/time.tools";
import { DAYS, TIMESTAMP_FORMAT, TsDuration, TsParam, UnixTimestamp, toTimestamp, valueOf } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { minimum } from "@cervval.js/common/src/tools/timestamps.tools.number";
import { asDate, diff, durationValue, format, toDate } from "@cervval.js/common/src/tools/timestamps.tools.tsdate";
import { TimestampTools } from "@cervval.js/common/tools";
import { DateOrDuration, Second, SortedTimeSlotArray, SortedWorkCalendarExceptionArray, TimeSlot, WeekTimeSlots, WithID, WorkCalendar, WorkCalendarCursor, WorkCalendarDay, WorkCalendarDefaultWeek, WorkCalendarExceptionWeek, WorkCalendarOptions, WorkCalendarPartition, WorkCalendarPeriod, WorkCalendarPeriodType, WorkDateType, toSecond } from "./calendars.definition";
import { WorkCalendarHelper } from "./calendars.helper";

// #region Timeline
/**
 * Typeguard WorkCalendarExceptionWeek
 */
export function isExceptionWeek(item: WorkCalendarExceptionWeek | WorkCalendarDefaultWeek): item is WorkCalendarExceptionWeek {
    return (item as WorkCalendarExceptionWeek).start !== undefined && (item as WorkCalendarExceptionWeek).finish !== undefined;
}

/**
 * Typeguard WorkCalendarDefaultWeek
 */
export function isDefaultWeek(item: WorkCalendarExceptionWeek | WorkCalendarDefaultWeek): item is WorkCalendarDefaultWeek {
    return (item as WorkCalendarExceptionWeek).start === undefined && (item as WorkCalendarExceptionWeek).finish === undefined;
}

/**
 * Check that an exceptionItem is valid
 */
export function isExceptionWeekValid(item: WorkCalendarExceptionWeek | WorkCalendarDefaultWeek): boolean {
    if (isExceptionWeek(item)) {
        const delta: TsDuration = { second: valueOf(item.finish) - valueOf(item.start) };
        const nDay = durationValue(delta, "day");
        if (nDay) {
            const firstDay = toDate(item.start).weekday;
            for (let d = 0; d < Math.min(nDay, 7); d++) {
                const currentDay = (firstDay + d - 1) % 7 + 1;
                if (getProp(item.week, currentDay) === undefined) {
                    return false;
                }
            }
            return true;
        }
    }
    return false;
}

/**
 * Sort the exception by day of occurence
 * @param exceptions 
 * @returns 
 */
export function sortExceptionWeeks(exceptions: WorkCalendarExceptionWeek[]): SortedWorkCalendarExceptionArray {
    exceptions.sort((a, b) => {
        const startFirst: number = valueOf(a.start) - valueOf(b.start);
        return startFirst === 0 ? valueOf(a.finish) - valueOf(b.finish) : startFirst;
    });
    for (const exception of exceptions) {
        for (const day in exception.week) {
            const exceptionDay = getProp(exception.week, day) as WorkCalendarDay | undefined;
            if (exceptionDay) {
                sortTimeSlots(exceptionDay.timeslots);
            }
        }
    }
    return exceptions as SortedWorkCalendarExceptionArray;
}

/**
 * Sort the time slots by starting time
 */
export function sortTimeSlots(timeslots: TimeSlot[]): SortedTimeSlotArray {
    timeslots.sort((a, b) => a.start - b.start);
    return timeslots as SortedTimeSlotArray;
}

/**
 * Calculate the number of second in the timeslots
 * @param timeslots 
 * @returns 
 */
function calculateSeconds(timeslots: TimeSlot[]): Second {
    return toSecond(timeslots.reduce((a, b) => { return a + (b.finish - b.start); }, 0));
}

/**
 * Build the timeline from a calendar
 * @param calendar 
 * @returns 
 */
export function buildTimeline(calendar: WorkCalendar): Map<UnixTimestamp | null, WorkCalendarPartition> {
    _calculateCalendarItemDurationIfNeeded(calendar.default);
    const startTickMap: Map<UnixTimestamp, WorkCalendarExceptionWeek[]> = new Map();
    const finishTickMap: Map<UnixTimestamp, WorkCalendarExceptionWeek[]> = new Map();
    for (const calendarItem of calendar.exceptions) {
        if (!isExceptionWeekValid(calendarItem)) {
            throw new Error("WorkCalendarExceptionWeek is not valid:");
        }
        _calculateCalendarItemDurationIfNeeded(calendarItem);
        if (!startTickMap.has(calendarItem.start)) {
            startTickMap.set(calendarItem.start, []);
        }
        if (!finishTickMap.has(calendarItem.finish)) {
            finishTickMap.set(calendarItem.finish, []);
        }
        startTickMap.get(calendarItem.start)?.push(calendarItem);
        finishTickMap.get(calendarItem.finish)?.push(calendarItem);
    }

    const ticks = [...Array.from(startTickMap.keys()), ...Array.from(finishTickMap.keys())].sort((a, b) => diff(b, a, "second"));
    const timeline: Map<UnixTimestamp | null, WorkCalendarPartition> = new Map();
    // Build the first default partition
    const firstPartition = buildPartition([calendar.default], undefined, calendar.exceptions[0]?.start);
    timeline.set(null, firstPartition);

    let lastPartition = firstPartition;
    for (let t = 0; t < ticks.length; t++) {
        const tick = ticks[t];
        const nextTick: UnixTimestamp | undefined = ticks[t + 1];
        const startItem = startTickMap.get(tick);
        const finishItem = finishTickMap.get(tick);
        if (startItem) {
            const to = (nextTick ? minimum(startItem[0].finish, nextTick) : startItem[0].finish) ?? undefined;
            const items: (WorkCalendarDefaultWeek | WorkCalendarExceptionWeek)[] = [];
            for (const item of lastPartition.calendarItems) {
                if (isDefaultWeek(item) || !finishItem?.includes(item)) {
                    items.push(item);
                }
            }
            const newPartition = buildPartition([...startItem, ...items], startItem[0].start, to, lastPartition);
            lastPartition = newPartition;
            timeline.set(tick, newPartition);
        } else if (finishItem) {
            const to = nextTick;
            const items: (WorkCalendarDefaultWeek | WorkCalendarExceptionWeek)[] = [];
            for (const item of lastPartition.calendarItems) {
                if (isDefaultWeek(item) || !finishItem.includes(item)) {
                    items.push(item);
                }
            }
            const newPartition = buildPartition(items, tick, to, lastPartition);
            lastPartition = newPartition;
            timeline.set(tick, newPartition);
        }
    }
    return timeline;
}

/**
 * Build a timeline partition
 * @param calendarItems 
 * @param from 
 * @param to 
 * @param previous 
 * @returns 
 */
function buildPartition(calendarItems: (WorkCalendarDefaultWeek | WorkCalendarExceptionWeek)[], from?: UnixTimestamp, to?: UnixTimestamp, previous?: WorkCalendarPartition): WorkCalendarPartition {
    const newPartition: WorkCalendarPartition = {
        from,
        to,
        calendarItems,
        _duration: from && to ? _calculatePartitionDuration(calendarItems[0], from, to) : undefined,
        previous
    };
    if (previous) {
        previous.next = newPartition;
    }
    return newPartition;
}

/**
 * Calculate a calendar item (typical week of a partition) duration from its typical days
 * @param calendarItem 
 */
function _calculateCalendarItemDurationIfNeeded(calendarItem: WorkCalendarExceptionWeek | WorkCalendarDefaultWeek): void {
    let weekDuration = 0;
    for (let d = 1; d <= 7; d++) {
        const itemDay = getProp(calendarItem.week, d) as WorkCalendarDay | undefined;
        if (itemDay && itemDay._duration === undefined) {
            itemDay._duration = calculateSeconds(itemDay.timeslots);
        }
        if (itemDay?._duration) {
            weekDuration += itemDay._duration;
        }
    }
    calendarItem._duration = toSecond(weekDuration);
}

/**
 * Calculate the partition duration
 * @param calendarItem 
 * @param from 
 * @param to 
 * @returns 
 */
function _calculatePartitionDuration(calendarItem: WorkCalendarDefaultWeek | WorkCalendarExceptionWeek, from: UnixTimestamp, to: UnixTimestamp): Second {
    const fromDay = toDate(from).weekday;
    const toDay = toDate(to).weekday;

    let duration = toSecond(0);
    let shiftedFrom = valueOf(from);
    // Align period to monday 00:00
    if (fromDay !== 7) {
        for (let d = 0; d < 7; d++) {
            const currentDay = (fromDay + d - 1) % 7 + 1;
            const currentCalendarDay = getProp(calendarItem.week, currentDay) as WorkCalendarDay | undefined;
            if (shiftedFrom + SECONDS_IN_ONE_DAY > valueOf(to)) {
                return duration;
            }
            duration = toSecond(duration + (currentCalendarDay?._duration ? currentCalendarDay._duration : 0));
            shiftedFrom += SECONDS_IN_ONE_DAY;
            if (currentDay === 7) {
                break;
            }
        }
    }
    // Add weeks
    if (calendarItem._duration && valueOf(to) - shiftedFrom > SECONDS_IN_ONE_WEEK) {
        const weeksInDelta = Math.floor((valueOf(to) - shiftedFrom) / SECONDS_IN_ONE_WEEK);
        duration = toSecond(duration + weeksInDelta * calendarItem._duration);
        shiftedFrom += weeksInDelta * SECONDS_IN_ONE_WEEK;
    }
    // Add remaining days to reach "to"
    if (toDay !== 7) {
        for (let d = 1; d < toDay; d++) { // TODO <= or < ?
            const currentCalendarDay = getProp(calendarItem.week, d) as WorkCalendarDay | undefined;
            duration = toSecond(duration + (currentCalendarDay?._duration ? currentCalendarDay._duration : 0));
        }
    }
    return duration;
}

/** */
export function buildCalendars<Calendar extends WithID, Exception extends WithID, Week extends WeekTimeSlots>(calendarOptions: WorkCalendarOptions<Calendar, Exception, Week>): WorkCalendar[] | null {
    const o = calendarOptions;
    const calendars: WorkCalendar[] = [];
    for (const calendar of o.calendars) {
        const formatedCalendar: Partial<WorkCalendar> = { id: calendar.id };
        const defaultWeek = o.weeks.find(w => w.id === calendar[o.calendarWeekIdKey] as unknown as number);
        if (defaultWeek === undefined) { return null; }
        for (const day in defaultWeek.week) {
            sortTimeSlots(getProp<{ timeslots: TimeSlot[] }>(defaultWeek.week, day).timeslots);
        }
        formatedCalendar.default = {
            week: defaultWeek.week
        } as unknown as WorkCalendarDefaultWeek;

        const formatedExceptions: WorkCalendarExceptionWeek[] = [];
        const calendarExceptions = o.exceptions.filter(e => e[o.exceptionCalendarIdKey] as unknown as number === calendar.id);
        for (const exception of calendarExceptions) {
            const exceptionWeek = o.weeks.find(w => w.id === exception[o.exceptionWeekIdKey] as unknown as number);
            if (exceptionWeek === undefined) { return null; }
            const exceptionStart = exception[o.exceptionStartKey] as unknown as number;
            const exceptionFinish = exception[o.exceptionFinishKey] as unknown as number;
            if (exceptionStart === undefined || !isNumber(exceptionStart)) { return null; }
            if (exceptionFinish === undefined || !isNumber(exceptionFinish)) { return null; }
            formatedExceptions.push({
                week: exceptionWeek.week,
                start: toTimestamp(exceptionStart),
                finish: toTimestamp(exceptionFinish)
            });
        }
        formatedCalendar.exceptions = sortExceptionWeeks(formatedExceptions);
        calendars.push(formatedCalendar as WorkCalendar);
    }
    return calendars;
}

// #endregion Timeline
// #region Cursor

/**
 * Build a calendar cursor
 */
export function buildCalendarCursor(calendarTimeline: WorkCalendarHelper, start: TsParam, target: DateOrDuration, dateType: WorkDateType): WorkCalendarCursor {
    const startDate = asDate(start);

    // compute cursor direction
    let direction: -1 | 0 | 1;
    if (target.duration != null) {
        direction = Math.sign(target.duration) as -1 | 0 | 1;
    } else {
        direction = Math.sign(target.date.uts - startDate.uts) as -1 | 0 | 1;
    }
    if (direction === 0) {
        direction = dateType === WorkDateType.FINISH ? -1 : 1;
    }

    return {
        start: startDate,
        target,
        dateType,
        direction,
        result: {
            targetReached: false,
            weeks: 0,
            days: 0,
            additionalSeconds: 0,
            elapsed: 0
        },
        position: startDate,
        currentPartition: calendarTimeline.getPartitionAt(startDate.uts)
    };
}

/**
 * run calendar cursor until target is reached
 */
export function runCalendarCursor(cursor: WorkCalendarCursor): void {
    while (!cursor.result.targetReached) {
        elapseWeeks(cursor);

        while (!cursor.result.targetReached) {
            const etsOutput = elapseTimeSlots(cursor);
            if (etsOutput.changedPartition) {
                // we may want to elapseWeeks again
                break;
            }
        }
    }
}

/**
 * ! DEBUG
 * @param cursor 
 */
export function _showCursorDebug(cursor: WorkCalendarCursor, stepName: string, force = false): void {
    console.log(">>> Cursor Status ", stepName);
    console.log("> Start:", format(cursor.start, "ccc " + TIMESTAMP_FORMAT));
    console.log("> Target:", cursor.target.date == null ? cursor.target.duration / SECONDS_IN_ONE_HOUR + " hours" : format(cursor.target.date, "ccc " + TIMESTAMP_FORMAT));
    console.log("> Result:", JSON.stringify(cursor.result));
    console.log("> Position:", format(cursor.position, "ccc " + TIMESTAMP_FORMAT));
}

/**
 * Elapse a maximum of weeks of the cursor
 */
export function elapseWeeks(cursor: WorkCalendarCursor): void {
    if (cursor.result.targetReached) { return; }

    const calendarItem = cursor.currentPartition.calendarItems[0];
    if (calendarItem === undefined || calendarItem._duration === undefined) {
        throw new Error("Partition was not properly initialized: it has no calendarItem and/or no week duration was computed");
    }
    const partitionLimit = cursor.direction < 0 ? cursor.currentPartition.from : cursor.currentPartition.to;

    let weekCount = 0;
    if (cursor.target.date != null) {
        weekCount = TimestampTools.diff(cursor.position, cursor.target.date, "week");
    } else {
        if (calendarItem._duration === 0) {
            if (partitionLimit == null) { return; }
            // move directly to end of partition
            cursor.position = toDate(partitionLimit);
        } else {
            weekCount = (cursor.target.duration - cursor.result.elapsed) / calendarItem._duration;
        }
    }
    weekCount = weekCount > 0 ? Math.floor(weekCount) : Math.ceil(weekCount);
    if (weekCount === 0) {
        return;
    }

    if (partitionLimit != null) {
        // limit to current partition
        let partitionWeekCount = TimestampTools.diff(cursor.position, partitionLimit, "week");
        partitionWeekCount = partitionWeekCount > 0 ? Math.floor(partitionWeekCount) : Math.ceil(partitionWeekCount);
        if (Math.abs(partitionWeekCount) < Math.abs(weekCount)) {
            weekCount = partitionWeekCount;
        }
    }

    if (weekCount !== 0) {
        cursor.result.weeks += weekCount;
        cursor.result.elapsed += weekCount * calendarItem._duration;
        cursor.position = cursor.position.add({ week: weekCount });

        ensureCursorDirectionIsCorrect(cursor);
    }
}

/** ensure direction is correct if target is already reached but date is not yet adjusted to calendar */
function ensureCursorDirectionIsCorrect(cursor: WorkCalendarCursor): void {
    if (cursor.target.duration != null && cursor.target.duration === cursor.result.elapsed ||
        cursor.target.date != null && cursor.target.date.uts === cursor.position.uts) {
        cursor.direction = cursor.dateType === WorkDateType.FINISH ? -1 : 1;
    }
}

/**
 * Elapse all possible timeslots at cursor position on cursor position day
 * complexity is disabled because I consider the comments reduce the complexity and moving code to functions may not help
 * TODO BGS optimize tsDate related performances
 */
// eslint-disable-next-line complexity
export function elapseTimeSlots(cursor: WorkCalendarCursor): { changedPartition: boolean } {
    const output = { changedPartition: false };
    if (cursor.result.targetReached) { return output; }

    const direction = cursor.direction;
    let positionDay = cursor.position;
    if (direction < 0 && cursor.position.secondOfDay === 0) {
        // on midnight and direction <0 => go to previous day
        positionDay = positionDay.subtract({ second: 1 });
    }
    // switch to next partition if needed
    const nextPartition = direction < 0 ? cursor.currentPartition.previous : cursor.currentPartition.next;
    if (nextPartition != null && TimestampTools.isInside(positionDay.uts, nextPartition.from, nextPartition.to)) {
        cursor.currentPartition = nextPartition;
        output.changedPartition = true;
    }

    const calendarItem = _getCalendarItem(cursor.currentPartition);
    const calendarDay: WorkCalendarDay = getProp(calendarItem.week, positionDay.weekday);
    if (calendarDay?.timeslots === undefined || calendarDay?._duration === undefined) {
        throw new Error(`CalendarItem was not properly initialized: day ${positionDay.weekday} does not have timeslot and/or precomputed duration`);
    }

    // TODO elapseFullDay if possible

    let previousSlot: TimeSlot | undefined = undefined;
    const timeslots = direction > 0 ? calendarDay.timeslots : [...calendarDay.timeslots].reverse();
    for (const timeslot of timeslots) {
        try {
            const position = cursor.position;
            let slotStart = positionDay.setValues({ secondOfDay: timeslot.start });
            let slotFinish = positionDay.setValues({ secondOfDay: timeslot.finish });
            if (direction > 0 && position >= slotFinish || direction < 0 && position <= slotStart) {
                // if timeslot is out of the range (after or before the position depending of the direction), pass it
                continue;
            }
            if (direction > 0 && position > slotStart) {
                // we are inside slot => truncate slot
                slotStart = position;
            } else if (direction < 0 && position < slotFinish) {
                // we are inside slot => truncate slot
                slotFinish = position;
            }
            if (cursor.target.date != null) {
                // target is a date

                if (direction > 0 && cursor.target.date <= slotStart) {
                    // date is before slot => adjust the position to slot start
                    if (cursor.dateType === WorkDateType.START) {
                        cursor.position = slotStart;
                        cursor.result.targetReached = true;
                        return output;// target reached
                    } else {
                        if (previousSlot == null) {
                            // before first slot => day before => not reached
                            cursor.position = positionDay.beginOf("day");
                            cursor.direction = -1;// go backward
                        } else {
                            // between this slot and previous slot => reached
                            cursor.position = positionDay.setValues({ secondOfDay: previousSlot.finish });
                            cursor.result.targetReached = true;
                        }
                        return output;
                    }
                }
                if (direction < 0 && cursor.target.date >= slotFinish) {
                    // date is after slot => adjust the position to slot finish
                    if (cursor.dateType === WorkDateType.FINISH) {
                        cursor.position = slotFinish;
                        cursor.result.targetReached = true;
                        return output;// target reached
                    } else {
                        // not reached but no need to continue
                        if (previousSlot == null) {
                            // after last slot
                            cursor.position = positionDay.beginOfNext("day");
                            cursor.direction = 1;// go forward
                        } else {
                            // between this slot and previous slot => reached
                            cursor.position = positionDay.setValues({ secondOfDay: previousSlot.start });
                            cursor.result.targetReached = true;
                        }
                        return output;
                    }
                }

                let targetReached = false;
                if (direction > 0 && cursor.target.date <= slotFinish) {
                    // target is inside slot => truncate slot
                    if (cursor.target.date.uts === slotFinish.uts && cursor.dateType === WorkDateType.START) {
                        // not reached yet => go to next slot start
                    } else {
                        targetReached = true;
                    }
                    slotFinish = cursor.target.date;
                } else if (direction < 0 && cursor.target.date >= slotStart) {
                    // target is inside slot => truncate slot
                    if (cursor.target.date.uts === slotStart.uts && cursor.dateType === WorkDateType.FINISH) {
                        // not reached yet => go to next slot finish
                    } else {
                        targetReached = true;
                    }
                    slotStart = cursor.target.date;
                }
                if (slotStart > slotFinish) {
                    // slot is invalid - this should never happen
                    getLogConsole().error("slot is invalid");
                    continue;
                }
                // increment result and adjust position
                const duration = direction * TimestampTools.diff(slotStart, slotFinish, "second");
                cursor.result.additionalSeconds += duration;
                cursor.result.elapsed += duration;
                cursor.position = direction < 0 ? slotStart : slotFinish;
                if (targetReached) {
                    cursor.result.targetReached = true;
                    return output;
                }
            } else if (cursor.target.duration != null) {
                // target is a duration

                const remainingDuration = Math.abs(cursor.target.duration - cursor.result.elapsed);
                const slotDuration = TimestampTools.diff(slotStart, slotFinish, "second");

                if (slotDuration >= remainingDuration) {
                    // duration is reached inside slot
                    cursor.result.additionalSeconds += remainingDuration * direction;
                    cursor.result.elapsed += remainingDuration * direction;

                    // adjust position
                    const offset = direction * (remainingDuration - slotDuration);
                    cursor.position = (direction < 0 ? slotStart : slotFinish).add({ second: offset });
                    if (slotDuration === remainingDuration &&
                        (direction < 0 && cursor.dateType === WorkDateType.FINISH || direction > 0 && cursor.dateType === WorkDateType.START)) {
                        // direction < 0 : we are on slot start => not reached yet => go to next slot finish
                        // direction > 0 : we are on slot finish => not reached yet => go to next slot start
                    } else {
                        cursor.result.targetReached = true;
                        return output; // target reached
                    }
                } else {
                    // slot duration is not enough
                    cursor.result.additionalSeconds += slotDuration * direction;
                    cursor.result.elapsed += slotDuration * direction;
                    cursor.position = direction < 0 ? slotStart : slotFinish;
                }
            }
        } finally {
            previousSlot = timeslot;
        }
    }
    // target not reached => move to "next" day
    const nextDay = direction < 0 ? positionDay.beginOf("day") : positionDay.beginOfNext("day");
    if (cursor.target.date != null) {
        if (direction > 0 && cursor.dateType === WorkDateType.FINISH && nextDay > cursor.target.date) {
            // going beyond => stop on last timeslot of day
            if (previousSlot != null) {
                cursor.position = positionDay.setValues({ secondOfDay: previousSlot.finish });
            }
            cursor.result.targetReached = true;
            return output;
        }
        if (direction < 0 && cursor.dateType === WorkDateType.START && nextDay < cursor.target.date) {
            // going beyond => stop on first timeslot of day
            if (previousSlot != null) {
                cursor.position = positionDay.setValues({ secondOfDay: previousSlot.start });
            }
            cursor.result.targetReached = true;
            return output;
        }
    }
    cursor.position = nextDay;
    return output;
}

/**
 * Get calendarItem and handle error
 */
function _getCalendarItem(calendarPartition: WorkCalendarPartition): WorkCalendarDefaultWeek | WorkCalendarExceptionWeek {
    const calendarItem = calendarPartition.calendarItems[0];
    if (calendarItem === undefined || calendarItem._duration === undefined) {
        throw new Error("Partition was not properly initialized: it has no calendarItem and/or no week duration was computed");
    }
    return calendarItem;
}

/** Run the calendar helper to get the full period englobing the given instant, and whether it's worked or not */
export function getPeriodInfoAtInstant(helper: WorkCalendarHelper, instant: UnixTimestamp): WorkCalendarPeriod {
    const date = toDate(instant);


    const partition = helper.getPartitionAt(date.uts);
    const calendarItem = _getCalendarItem(partition);
    const daySlots = calendarItem.week[date.weekday as DAYS];

    if (daySlots?.timeslots != null) {
        // If the slots are defined, check if the given instant is in one of them
        for (const slot of daySlots.timeslots) {
            const slotStart = date.setValues({ secondOfDay: slot.start });
            const slotFinish = date.setValues({ secondOfDay: slot.finish });
            if (isInside(date, slotStart, slotFinish)) {
                // If match found, means the period is worked
                return { type: WorkCalendarPeriodType.WORKED, start: slotStart.uts, finish: slotFinish.uts };
            }
        }
    }

    // If there, it means the given instant is in a not worked period.
    const notWorkedPeriod: WorkCalendarPeriod = {
        type: WorkCalendarPeriodType.NOT_WORKED
    };
    // Searching for the next working period's start to find the not worked period's finish boundary
    let cursor = buildCalendarCursor(helper, date, { duration: toSecond(0) }, WorkDateType.START);
    runCalendarCursor(cursor);
    notWorkedPeriod.finish = cursor.position.uts;

    // Searching for the previous working period's finish to find the not worked period's start boundary
    cursor = buildCalendarCursor(helper, date, { duration: toSecond(-0) }, WorkDateType.FINISH);
    runCalendarCursor(cursor);
    notWorkedPeriod.start = cursor.position.uts;

    return notWorkedPeriod;
}
